import * as Sentry from '@sentry/react';
import Axios from 'axios';
import { history } from 'components/router/history';
import { ErrorCodes } from 'constants/error-codes';
import { IS_PREVIEW } from 'constants/runtime';

const boot = () => {
    Sentry.init({
        dsn: import.meta.env.VITE_DASHBOARD_SENTRY_DSN,
        environment: getSentryEnv(),
        normalizeDepth: 10,
        integrations: [
            Sentry.reactRouterV5BrowserTracingIntegration({ history }),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: ['dashboard-sentry-application-key'],
                behaviour: 'drop-error-if-contains-third-party-frames',
            }),
            Sentry.replayIntegration(),
        ],
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.type === 'debug') {
                return null;
            }

            const ignoreThirdParty = ['launchdarkly', 'posthog', 'cdn-cgi', 'segment'].some(
                value =>
                    breadcrumb.message?.toLowerCase().includes(value) ||
                    breadcrumb.data?.url?.toLowerCase().includes(value),
            );

            if (ignoreThirdParty) {
                return null;
            }

            return breadcrumb;
        },
        beforeSend: (event, hint) => {
            const originalError = hint.originalException;
            const ignoreThirdPartyModules = (event.exception?.values ?? []).some(exception =>
                (exception.stacktrace?.frames ?? []).some(frame =>
                    [
                        'posthog-js',
                        'chart.js',
                        'maps',
                        'flatfile',
                        'tinymce',
                        'intercom',
                        'beacon',
                        'stripe',
                        'sentry',
                    ].some(sdk => frame.filename?.includes(sdk)),
                ),
            );

            if (ignoreThirdPartyModules) {
                return null;
            }

            if (Axios.isAxiosError(originalError)) {
                if (
                    [ErrorCodes.UNAUTHORIZED, ErrorCodes.FORBIDDEN].some(
                        code => originalError.response?.status === code,
                    )
                ) {
                    return null;
                }

                event.message = originalError.message;
                event.contexts = {
                    ...event.contexts,
                    axiosRequest: originalError.request,
                    axiosResponse: originalError.response?.data,
                };
            }

            return event;
        },
        ignoreErrors: ['LaunchDarklyFlagFetchError', 'CanceledError'],
        denyUrls: ['api.zapier.com', 'cdn.segment.com'],
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/givebutter\.com\/api/,
            /^https:\/\/givebutter\.test\/api/,
            /^https:\/\/legendairy\.xyz\/api/,
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 0.2,
        profilesSampleRate: 0.2,
    });
};

function getSentryEnv() {
    const branch = import.meta.env.VITE_VERCEL_GIT_COMMIT_REF;

    if (import.meta.env.PROD && branch) {
        if (branch.includes('demo')) {
            return 'demo';
        }

        if (branch.includes('staging')) {
            return 'staging';
        }

        if (IS_PREVIEW) {
            return 'preview';
        }

        return 'production';
    }

    return 'dev';
}

export { boot };
